import React, {
	createContext,
	useContext,
	useReducer,
	useEffect,
	useCallback,
} from 'react';
import { getTemplabourFilters } from 'utils/apiRequests';
import filterNames from 'utils/filterNames';

// Define the initial state
//Options is for dropdowns
//For Range is array with two values End and Start
const DEFAULTS = {
	RANGEMIN: 0,
	RANGEMAX: 1000000,
};
const DEFAULTSOPTIONS = [{ label: 'All', value: 'All' }];
const initialState = {
	workerTypeOptions: [],
	stateOptions: [],
	jobClassOptions: [],
	totalEmpRange: [],
	sumSpendOverRange: [],
	grossAmountRange: [],
	blsYearOptions: [],
	shouldRefetch: false,
	loading: true,
	error: null,
};
// Define actions
const actionTypes = {
	SET_FILTER: 'SET_FILTER',
	SET_FILTER_OPTIONS: 'SET_FILTER_OPTIONS',
	SET_REFETCH: 'SET_REFETCH',
	FETCH_FILTER_OPTIONS_REQUEST: 'FETCH_FILTER_OPTIONS_REQUEST',
	FETCH_FILTER_OPTIONS_SUCCESS: 'FETCH_FILTER_OPTIONS_SUCCESS',
	SET_MULTIPLE_FILTER_OPTIONS: 'SET_MULTIPLE_FILTER_OPTIONS',
	SET_FILTER_OPTIONS_SUCCESS: 'SET_FILTER_OPTIONS_SUCCESS',
	FETCH_FILTER_OPTIONS_FAILURE: 'FETCH_FILTER_OPTIONS_FAILURE',
	// Add more actions as needed
};

// Define the reducer function
const filterReducer = (state, action) => {
	switch (action.type) {
		case actionTypes.SET_FILTER:
			return {
				...state,
				[action.filterName]: action.value,
			};
		case actionTypes.SET_FILTER_OPTIONS:
			return {
				...state,
				filterOptions: action.filterOptions,
			};
		case actionTypes.SET_REFETCH:
			return {
				...state,
				shouldRefetch: !state.shouldRefetch,
			};
		case actionTypes.FETCH_FILTER_OPTIONS_REQUEST:
			return {
				...state,
				loading: true,
				error: null,
			};
		case actionTypes.FETCH_FILTER_OPTIONS_SUCCESS:
			return {
				...state,
				...action,
				filterOptions: action.filterOptions,
				loading: false,
			};
		case actionTypes.SET_FILTER_OPTIONS_SUCCESS:
			return {
				...state,
				...action,
				filterOptions: action.filterOptions,
			};
		case actionTypes.SET_MULTIPLE_FILTER_OPTIONS:
			return {
				...state,
				...action,
				...action.filterOptions,
			};
		case actionTypes.FETCH_FILTER_OPTIONS_FAILURE:
			return {
				...state,
				loading: false,
				error: action.error,
			};
		// Handle other actions if needed
		default:
			return state;
	}
};

// Create the context
const FilterContext = createContext();

// Create the context provider component
const FilterContextProvider = ({ children }) => {
	const [state, dispatch] = useReducer(filterReducer, initialState);
	// Define actions for setting filters
	const setFilter = useCallback((filterName, value) => {
		dispatch({
			type: actionTypes.SET_FILTER_OPTIONS_SUCCESS,
			filterName,
			value,
		});
	}, []);

	const setMultipleFilters = useCallback(changedFilters => {
		dispatch({
			type: actionTypes.SET_FILTER_OPTIONS_SUCCESS,
			...changedFilters,
		});
	}, []);
	const setRefetch = useCallback(() => {
		dispatch({ type: actionTypes.SET_REFETCH });
	}, []);

	// Define async action for fetching filter options
	const fetchFilterOptions = useCallback(async () => {
		dispatch({ type: actionTypes.FETCH_FILTER_OPTIONS_REQUEST });
		try {
			// // Perform API call to get filter options
			// const response = await fetch('/api/filters'); // Replace with your API endpoint
			// const data = await response.json();
			getTemplabourFilters()
				.then(response => {
					dispatch({
						type: actionTypes.FETCH_FILTER_OPTIONS_SUCCESS,
						workerTypeOptions: [
							...response.data.workerTypeOptions.map(ele => ({
								label: ele,
								value: ele,
							})),
						],
						stateOptions: [
							...response.data.stateOptions.map(ele => ({
								label: ele,
								value: ele,
							})),
						],
						jobClassOptions: [
							...response.data.jobClassOptions.map(ele => ({
								label: ele,
								value: ele,
							})),
						],
						// scatterPlotEmpTotalMin: response.data.toT_EMP_Min,
						// scatterPlotEmpTotalMax: response.data.toT_EMP_Max,
						totalEmpRange: [
							response.data.toT_EMP_Min || DEFAULTS.RANGEMIN,
							response.data.toT_EMP_Max || DEFAULTS.RANGEMAX,
						],
						sumSpendOverRange: [
							response.data.sumSpendOverJobTitleMin || DEFAULTS.RANGEMIN,
							response.data.sumSpendOverJobTitleMax || DEFAULTS.RANGEMAX,
						],
						grossAmountRange: [
							response.data.grossAmountMin || DEFAULTS.RANGEMIN,
							response.data.grossAmountMax || DEFAULTS.RANGEMAX,
						],
						blsYearOptions:
							response.data.blsYr.map(ele => ({
								label: ele,
								value: ele,
							})) || DEFAULTSOPTIONS,

						cagrOptions:
							response.data.cagR_Column.map(ele => ({
								label: ele,
								value: ele,
							})) || DEFAULTSOPTIONS,
						[filterNames.scatterPlotState]: [response.data.stateOptions[0]],
						[filterNames.scatterPlotWorkerType]: [
							response.data.workerTypeOptions[0],
						],
						[filterNames.scatterPlotCagr]: {
							label: response.data.cagR_Column[0],
							value: response.data.cagR_Column[0],
						},
						[filterNames.scatterPlotBLSYear]: [response.data.blsYr[0]],
						[filterNames.scatterPlotEmpTotalRange]: [
							Math.round(response.data.toT_EMP_Min),
							Math.round(response.data.toT_EMP_Max),
						],
						[filterNames.stackedBarChartJobClass]: [
							response.data.jobClassOptions[0],
						],
						[filterNames.stackedBarChartGrossAmountRange]: [
							Math.floor(response.data.grossAmountMin),
							Math.ceil(response.data.grossAmountMax),
						],
						[filterNames.boxWhiskerClientJobClass]: [
							response.data.jobClassOptions[0],
						],
						[filterNames.boxWhiskerClientSumSpendOverJobTitleRange]: [
							Math.floor(response.data.sumSpendOverJobTitleMin),
							Math.ceil(response.data.sumSpendOverJobTitleMax),
						],
						[filterNames.boxWhiskerBLSWorkerType]: [
							response.data.workerTypeOptions[0],
						],
						[filterNames.boxWhiskerBLSYear]: [response.data.blsYr[0]],
						[filterNames.mapStateWorkerType]: [
							response.data.workerTypeOptions[0],
						],
						[filterNames.mapStateBLSYear]: [response.data.blsYr[0]],
						[filterNames.mapCityWorkerType]: [
							response.data.workerTypeOptions[0],
						],
						[filterNames.mapCityBLSYear]: [response.data.blsYr[0]],
					});
				})
				.catch(error => {
					dispatch({
						type: actionTypes.FETCH_FILTER_OPTIONS_FAILURE,
						error: error.message || 'Error fetching chart data',
					});
				})
				.finally(() => {
					// dispatch({ type: actionTypes.FETCH_FILTER_OPTIONS_SUCCESS });
					dispatch({ type: actionTypes.SET_REFETCH });
				});
		} catch (error) {
			dispatch({
				type: actionTypes.FETCH_FILTER_OPTIONS_FAILURE,
				error: error.message || 'Error fetching filter options',
			});
		}
	}, []);

	// Fetch filter options on component mount
	useEffect(
		() => {
			fetchFilterOptions();
		},
		[fetchFilterOptions]
	);

	// Provide the state and actions to the children
	const contextValue = {
		state,
		actions: {
			setFilter,
			setRefetch,
			fetchFilterOptions,
			setMultipleFilters,
			// Add more actions as needed
		},
	};

	return (
		<FilterContext.Provider value={contextValue}>
			{children}
		</FilterContext.Provider>
	);
};

// Create a custom hook for using the context
const useFilterContext = () => {
	const context = useContext(FilterContext);
	if (!context) {
		throw new Error(
			'useFilterContext must be used within a FilterContextProvider'
		);
	}
	return context;
};

export { FilterContextProvider, useFilterContext };

// Usage

// import React from 'react';
// import { useFilterContext } from './FilterContextProvider';

// const Chart1 = () => {
//   const { state, actions } = useFilterContext();
//   const { chartOptions1, chartOptions2 } = state;
//   // Access other filters as needed
//   // Render chart based on filter values
//actions.setFilter sets filters based filterNames utlis
//   return (
//     <div>
//       <h2>Chart 1</h2>
//       {/* Render chart content */}
//       <p>Filter 1: {chartOptions1}</p>
//       <p>Filter 2: {chartOptions2}</p>
//     </div>
//   );
// };

// export default Chart1;
